import { Fragment } from 'react';
import { sprintf } from 'sprintf-js';
import { styled } from 'styled-components';

import type { ApiListingMiniExtra } from '#/api/types/ApiListingMini';
import { ExpandButton } from '#/components/ExpandButton/ExpandButton';
import { StyledVersionedRouterLink } from '#/components/Link/StyledVersionedRouterLink';
import { Table } from '#/components/Table/Table';
import { useAppConfig } from '#/config/AppConfigContext';
import { useIsClient } from '#/hooks/useIsClient';
import { useViewportBreakpointValue } from '#/hooks/viewport/useViewportBreakpointValue';
import { useI18n } from '#/i18n/hooks/useI18n';
import { useFetchOrgListings } from '#/modules/listing/orgs/hooks/useFetchOrgListings';
import { colors, spacing } from '#/theme/theme';
import { getWorkLocationString } from '#/utils/address/getWorkLocationString';
import { trackClicked } from '#/utils/analytics/track/trackClicked';
import { formatRelativeToNow } from '#/utils/date';

const Styledtable = styled(Table)<{ $stacked?: boolean }>`
  ${({ $stacked }) =>
    $stacked &&
    `tbody > tr:nth-of-type(odd) {
    background-color: ${colors.backgroundGrey};
  }
  tr > td {
    display: table-row;
  }`}
`;

function Listings({
  listingType,
  listings,
}: {
  listingType: 'JOB' | 'INTERNSHIP' | 'VOLOP' | 'EVENT';
  listings: ApiListingMiniExtra[];
}) {
  const { appConfig } = useAppConfig();
  const { i18n, locale } = useI18n();

  const isStacked = useViewportBreakpointValue({ xs: true, sm: false });
  const isClient = useIsClient();

  return (
    <Styledtable
      $stacked={isStacked}
      headings={[]}
      data={listings.map((listing) => [
        <StyledVersionedRouterLink
          key={listing.id}
          to={listing.url[locale]}
          onClick={() =>
            trackClicked('Current Listings on Org Page', {
              listing_type: listingType,
            })
          }
        >
          {listing.name}
        </StyledVersionedRouterLink>,

        <Fragment key="location">
          {!listing.address
            ? i18n('Online/Virtual')
            : getWorkLocationString({
                i18n,
                remoteOptions: {
                  remoteZone: listing.remoteZone,
                  remoteCountry: listing.remoteCountry,
                  remoteState: listing.remoteState,
                },
                address: listing.address,
              })}
          {listing.locationType === 'REMOTE' && (
            <span style={{ marginLeft: spacing[6] }}>
              {sprintf('(%(text)s)', { text: i18n('Remote') })}
            </span>
          )}
        </Fragment>,

        isClient
          ? i18n('Posted %(postedAt)s', {
              postedAt: formatRelativeToNow({
                date: listing.firstPublished,
                locale,
                dtOverride: appConfig.idealist.dtOverride,
              }),
            })
          : '...',
      ])}
    />
  );
}

type Props = {
  listingCount: number;
  listingType: 'JOB' | 'INTERNSHIP' | 'VOLOP' | 'EVENT';
  listings: ApiListingMiniExtra[];
  orgId: string;
};

export function ViewOrgPageTruncatedListings({
  listingCount,
  listingType,
  listings: listingsArg,
  orgId,
}: Props) {
  const { i18n } = useI18n();

  const { loading, listings, page, setPage } = useFetchOrgListings(
    listingsArg,
    listingType,
    orgId,
  );

  const hasMore = listings.length < listingCount;
  const toggleShowMore = () => {
    if (!loading) {
      trackClicked('View More Listings on Org Page', {
        listing_type: listingType,
        page: page,
      });
      setPage(page + 1);
    }
  };

  if (hasMore) {
    return (
      <>
        <div style={{ marginBottom: 10 }} data-qa-id="view-org-page-listings">
          <Listings listingType={listingType} listings={listings} />
        </div>

        <ExpandButton
          qaId="view-more-org-listings"
          onClick={() => toggleShowMore()}
          open={hasMore}
        >
          {i18n('View %(count)s more', {
            count: listingCount - listings.length,
          })}
        </ExpandButton>
      </>
    );
  }

  return (
    <div data-qa-id="view-org-page-listings">
      <Listings listingType={listingType} listings={listings} />
    </div>
  );
}
